import type { FeatureCollection, LineString } from 'geojson';
import type { LngLat } from './types.ts';
import { distanceM } from './geo.ts';

/**
 * Routing a buggy to the next tee along the cart paths.
 *
 * A straight line to the next tee is worse than useless on a golf course: it
 * points through a lake, across a green you are fenced off from, or into trees.
 * The paths exist as data, so the route should follow them.
 *
 * OpenStreetMap splits paths into many short ways which meet at shared nodes —
 * but "shared" is only usually exact, and a few centimetres of disagreement
 * would leave the network in disconnected islands with no route between them.
 * So vertices are welded together within a tolerance rather than compared for
 * equality.
 */

export interface Graph {
  /** Node coordinates, indexed by node id. */
  nodes: LngLat[];
  /** Adjacency: node id -> [neighbour id, distance in metres][]. */
  edges: Map<number, [number, number][]>;
}

/** Nodes within this distance of each other are the same junction. */
const WELD_M = 1.5;

export function buildGraph(paths: FeatureCollection | null): Graph {
  const nodes: LngLat[] = [];
  const edges = new Map<number, [number, number][]>();
  if (!paths?.features?.length) return { nodes, edges };

  // ~1.5m in degrees. Latitude is the tighter axis, so use it for both.
  const cell = WELD_M / 111_132;
  const grid = new Map<string, number[]>();

  const nodeAt = (p: LngLat): number => {
    const kx = Math.round(p[0] / cell);
    const ky = Math.round(p[1] / cell);
    // Check the neighbouring cells too: two points either side of a cell
    // boundary are still the same junction.
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const id of grid.get(`${kx + dx}:${ky + dy}`) ?? []) {
          if (distanceM(nodes[id], p) <= WELD_M) return id;
        }
      }
    }
    const id = nodes.length;
    nodes.push(p);
    const k = `${kx}:${ky}`;
    grid.set(k, [...(grid.get(k) ?? []), id]);
    return id;
  };

  const link = (a: number, b: number) => {
    if (a === b) return;
    const w = distanceM(nodes[a], nodes[b]);
    edges.set(a, [...(edges.get(a) ?? []), [b, w]]);
    edges.set(b, [...(edges.get(b) ?? []), [a, w]]);
  };

  for (const f of paths.features) {
    if (f.geometry?.type !== 'LineString') continue;
    const coords = (f.geometry as LineString).coordinates as LngLat[];
    let prev: number | null = null;
    for (const c of coords) {
      const id = nodeAt([c[0], c[1]]);
      if (prev != null) link(prev, id);
      prev = id;
    }
  }

  bridgeIslands(nodes, edges);
  return { nodes, edges };
}

/**
 * How far apart two path ends can be and still be the same route.
 *
 * OpenStreetMap traces cart paths in pieces, and the pieces very often do not
 * meet: a path stops at a road crossing, at a bridge, or simply where one
 * contributor's survey ended and another's began. At The Palms that left 1,180
 * nodes joined by 1,168 edges — fewer edges than nodes, meaning the network was
 * more than a dozen separate islands, and any route longer than a single hole
 * came back "disconnected".
 *
 * 30m is chosen to span those gaps without inventing a path across a lake. A
 * buggy can cross thirty metres of grass between two path ends; it cannot cross
 * a fairway to a path that merely looks close on a map, and larger tolerances
 * start producing exactly that.
 */
const BRIDGE_M = 30;

/**
 * Joins disconnected components at their closest pair of points.
 *
 * One SIDE of the bridge must be a dead end — a node with a single neighbour is
 * where a traced path actually stopped, and that is where a real gap is.
 *
 * The other side is any node at all, and that asymmetry is the whole trick.
 * Measured at The Palms, the gaps are 4 to 34 metres and almost all of them run
 * from a path's dead end to the MIDDLE of another path — a T-junction that
 * OpenStreetMap never noded. Requiring both sides to be dead ends looks tidier
 * and connects almost nothing: it left ten islands standing with gaps of four
 * metres between them.
 */
function bridgeIslands(nodes: LngLat[], edges: Map<number, [number, number][]>) {
  const componentOf = new Array<number>(nodes.length).fill(-1);
  let count = 0;

  for (let start = 0; start < nodes.length; start++) {
    if (componentOf[start] !== -1) continue;
    const id = count++;
    const stack = [start];
    componentOf[start] = id;
    while (stack.length) {
      const cur = stack.pop()!;
      for (const [nb] of edges.get(cur) ?? []) {
        if (componentOf[nb] === -1) {
          componentOf[nb] = id;
          stack.push(nb);
        }
      }
    }
  }
  if (count < 2) return;

  const deadEnds: number[] = [];
  for (let i = 0; i < nodes.length; i++) {
    if ((edges.get(i)?.length ?? 0) <= 1) deadEnds.push(i);
  }

  // Repeatedly join the two closest components until nothing is close enough.
  // Merging rather than a single pass matters: three islands in a row are only
  // reachable if each join is visible to the next round.
  for (let pass = 0; pass < count * 2; pass++) {
    let bestA = -1;
    let bestB = -1;
    let bestD = BRIDGE_M;
    for (const a of deadEnds) {
      for (let b = 0; b < nodes.length; b++) {
        if (componentOf[a] === componentOf[b]) continue;
        const d = distanceM(nodes[a], nodes[b]);
        if (d < bestD) { bestD = d; bestA = a; bestB = b; }
      }
    }
    if (bestA === -1) break;

    edges.set(bestA, [...(edges.get(bestA) ?? []), [bestB, bestD]]);
    edges.set(bestB, [...(edges.get(bestB) ?? []), [bestA, bestD]]);
    const from = componentOf[bestB];
    const to = componentOf[bestA];
    for (let i = 0; i < componentOf.length; i++) {
      if (componentOf[i] === from) componentOf[i] = to;
    }
  }
}

/** Closest point on segment ab to p, plus how far away it is. */
function nearestOnSegment(p: LngLat, a: LngLat, b: LngLat): { point: LngLat; dist: number } {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-18) return { point: a, dist: distanceM(p, a) };
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point: LngLat = [a[0] + t * dx, a[1] + t * dy];
  return { point, dist: distanceM(p, point) };
}

interface Snap {
  /** Node ids at each end of the segment we landed on. */
  a: number;
  b: number;
  point: LngLat;
  dist: number;
}

function snap(graph: Graph, p: LngLat, maxDistM: number): Snap | null {
  let best: Snap | null = null;
  for (const [a, list] of graph.edges) {
    for (const [b] of list) {
      if (b < a) continue; // each edge once
      const r = nearestOnSegment(p, graph.nodes[a], graph.nodes[b]);
      if (!best || r.dist < best.dist) best = { a, b, point: r.point, dist: r.dist };
    }
  }
  return best && best.dist <= maxDistM ? best : null;
}

export interface RouteResult {
  /** The line to draw, including the walk-on and walk-off stubs. */
  path: LngLat[];
  /** Length along the paths, metres. */
  distanceM: number;
  /** False when no path connects the two points — draw nothing, say why. */
  ok: boolean;
  reason?: 'no-paths' | 'too-far-from-path' | 'disconnected';
}

/** How far a buggy can reasonably be from a path and still be "on the network". */
const SNAP_TOLERANCE_M = 90;

/**
 * Shortest path from `from` to `to` along the cart network.
 *
 * Dijkstra rather than A*: a golf course is a few thousand nodes at most, the
 * difference is microseconds, and Dijkstra has no heuristic to get wrong.
 */
export function routeAlongPaths(graph: Graph, from: LngLat, to: LngLat): RouteResult {
  if (!graph.nodes.length) {
    return { path: [], distanceM: 0, ok: false, reason: 'no-paths' };
  }

  const s = snap(graph, from, SNAP_TOLERANCE_M);
  const e = snap(graph, to, SNAP_TOLERANCE_M);
  if (!s || !e) {
    return { path: [], distanceM: 0, ok: false, reason: 'too-far-from-path' };
  }

  // Start from whichever end of the snapped segment, cost = walk along it.
  const starts: [number, number][] = [
    [s.a, distanceM(s.point, graph.nodes[s.a])],
    [s.b, distanceM(s.point, graph.nodes[s.b])],
  ];
  const targets = new Map<number, number>([
    [e.a, distanceM(e.point, graph.nodes[e.a])],
    [e.b, distanceM(e.point, graph.nodes[e.b])],
  ]);

  const dist = new Map<number, number>();
  const prev = new Map<number, number>();
  // Simple binary-heap-free Dijkstra; the node count does not justify a heap.
  const queue = new Set<number>();
  for (const [id, d] of starts) {
    if (!dist.has(id) || d < dist.get(id)!) dist.set(id, d);
    queue.add(id);
  }

  let bestEnd: number | null = null;
  let bestTotal = Infinity;

  while (queue.size) {
    let cur = -1;
    let curD = Infinity;
    for (const id of queue) {
      const d = dist.get(id) ?? Infinity;
      if (d < curD) {
        curD = d;
        cur = id;
      }
    }
    if (cur === -1) break;
    queue.delete(cur);

    const finish = targets.get(cur);
    if (finish != null && curD + finish < bestTotal) {
      bestTotal = curD + finish;
      bestEnd = cur;
    }
    // Everything still queued is already further than the best finish.
    if (curD > bestTotal) break;

    for (const [nb, w] of graph.edges.get(cur) ?? []) {
      const nd = curD + w;
      if (nd < (dist.get(nb) ?? Infinity)) {
        dist.set(nb, nd);
        prev.set(nb, cur);
        queue.add(nb);
      }
    }
  }

  if (bestEnd == null) {
    return { path: [], distanceM: 0, ok: false, reason: 'disconnected' };
  }

  const chain: LngLat[] = [];
  for (let at: number | undefined = bestEnd; at != null; at = prev.get(at)) {
    chain.push(graph.nodes[at]);
  }
  chain.reverse();

  // Stubs from the buggy onto the network and from the network to the tee.
  const path: LngLat[] = [from, s.point, ...chain, e.point, to];

  return { path, distanceM: bestTotal, ok: true };
}
